import {useTranslationContext} from 'platform/components';

import {useCallback, useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {isNotNilOrEmpty, useBoolean} from 'shared/web';

import {useDataGridContext} from '../context/useDataGridContext';
import {ChoiceFilterOptions} from '../types/Api';
import {useHttpCalls} from './useHttpCalls';

const EMPTY_OPTIONS: ChoiceFilterOptions = [];

type UseLazyChoiceFilterOptionsProps = {
  columnKey: string;
};

/**
 * Lazily loads choice filter options and stores them in DataGrid state.
 */
export const useLazyChoiceFilterOptions = (props: UseLazyChoiceFilterOptionsProps) => {
  const context = useDataGridContext();
  const http = useHttpCalls();
  const t = useTranslationContext();

  const [isLoading, startLoading, stopLoading] = useBoolean();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [options, setOptions] = useState<ChoiceFilterOptions>(EMPTY_OPTIONS);

  const loadOptions = useCallback(() => {
    if (isNilOrEmpty(context?.gridCode) || isNilOrEmpty(props.columnKey)) {
      return Promise.resolve(EMPTY_OPTIONS);
    }

    if (isNotNilOrEmpty(options)) {
      setErrorMessage(null);
      stopLoading();
      return Promise.resolve(options);
    }

    startLoading();
    setErrorMessage(null);

    return http
      .getColumnFilterOptions(props.columnKey)
      .then((response) => {
        setErrorMessage(null);
        setOptions(response);
      })
      .catch(() => {
        setErrorMessage(t('general.errors.cannotLoadOptions'));
      })
      .finally(() => {
        stopLoading();
      });
  }, [context?.gridCode, http, options, props.columnKey, startLoading, stopLoading, t]);

  return {
    options,
    isLoading,
    errorMessage,
    loadOptions,
  };
};
